// src/components/modals/ViewRawMaterialModal.jsx
import { X } from 'lucide-react'
import { Button } from '../ui/Button'

const ViewRawMaterialModal = ({ isOpen, onClose, rawMaterial, unit }) => {
  // Don't render if not open or no raw material
  if (!isOpen || !rawMaterial) return null

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">View Raw Material</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information - Two Column Grid */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Material name
              </label>
              <p className="text-gray-900 font-medium">{rawMaterial.name}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Material Code
              </label>
              <p className="text-gray-900">
                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {rawMaterial.code}
                </span>
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Unit
              </label>
              <p className="text-gray-900">
                {unit ? (
                  <span className="inline-flex items-center">
                    {unit.name} 
                    <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {unit.abbreviation}
                    </span>
                  </span>
                ) : (
                  <span className="text-gray-500">Unit not found</span>
                )}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Threshold
              </label>
              <p className="text-gray-900">
                <span className="font-medium">{rawMaterial.threshold}</span>
                {unit && <span className="text-gray-600 ml-1">{unit.abbreviation}</span>}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Low stock alert threshold quantity
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Status
              </label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                rawMaterial.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {rawMaterial.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {rawMaterial.description && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Description
                </label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {rawMaterial.description}
                </p>
              </div>
            )}
          </div>

                {(rawMaterial.createdAt || rawMaterial.updatedAt) && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Metadata</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                  {rawMaterial.createdAt && (
                    <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">{formatDate(rawMaterial.createdAt)}</span>
                    </div>
                  )}
                  {rawMaterial.updatedAt && rawMaterial.updatedAt !== rawMaterial.createdAt && (
                    <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="text-gray-900">{formatDate(rawMaterial.updatedAt)}</span>
                    </div>
                  )}
                  {rawMaterial._id && (
                    <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="text-gray-900">{rawMaterial._id}</span>
                    </div>
                  )}
                  </div>
                </div>
                )}

                {/* Additional Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Stock Information</h4>
            <p className="text-sm text-blue-800">
              This material will trigger a low stock alert when inventory falls below{' '}
              <span className="font-medium">{rawMaterial.threshold}</span>
              {unit && <span> {unit.abbreviation}</span>}.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end p-6 pt-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewRawMaterialModal